import { handleApiResponse } from '@src/api/apiClient/axiosClient.ts';
import { dxDiagnosisResultApiClient } from '@src/api/company/dxDiagnosis/dxDiagnosisResultApiClient.ts';
import { Pagination } from '@src/api/types/pagination.ts';
import { DxDiagnosisResult } from '@src/api/types/shared/dxDiagnosis.ts';
// import SvgIconDownload from '@src/assets/images/common/icon/chat-action/SvgIconDownload';
import PaginationComponent from '@src/components/Pagination.tsx';
import { CompanyAuthContext } from '@src/context/auth/CompanyAuthContext';
import { DxDiagnosisResultStatus } from '@src/enum/dxDiagnosisResultStatus';
import { useToast } from '@src/features/toast/context/toastContext';
import { MAX_NUMBER_OF_QUESTIONS, QUESTIONS_PER_STEP } from '@src/modules/constants/dxDiagnosis';
import { COMPANY_URL } from '@src/routes/company';
import Button from '@src/ui/elements/button/Button.tsx';
// import DropdownCheckbox from '@src/ui/elements/form/DropdownCheckbox';
import Th from '@src/ui/elements/table/Th.tsx';
import Title from '@src/ui/elements/typography/Title.tsx';
import logger from '@src/utils/logger';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';

import DeleteConfirmModal from './components/DeleteConfirmModal';
// import DxDiagnosisMoreVertMenu from './components/DxDiagnosisMoreVertMenu';

const DxDiagnosisListForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dxDiagnosisResults, setDxDiagnosisResults] = useState<DxDiagnosisResult[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingResultId, setDeletingResultId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [exportFilters, setExportFilters] = useState<string[]>([
    'all',
    DxDiagnosisResultStatus.IN_PROGRESS.toString(),
    DxDiagnosisResultStatus.COMPLETED.toString(),
  ]);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const authContext = useContext(CompanyAuthContext);
  const { profile } = authContext || {};
  const toast = useToast();

  const loadDxDiagnosisResults = async (
    page = 1,
    sortField: string | null = null,
    sortDirection: 'asc' | 'desc' = 'asc',
  ) => {
    const params: { page: number; sort?: string; direction?: 'asc' | 'desc' } = {
      page,
    };
    if (sortField) {
      params.sort = sortField;
      params.direction = sortDirection;
    }
    const response = await dxDiagnosisResultApiClient.index(params);
    handleApiResponse(
      response,
      (data) => {
        setDxDiagnosisResults(data.data);
        setPagination(data.pagination);
      },
      (error) => {
        logger.error('診断データの取得に失敗しました:', error);
      },
    );
  };

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const page = parseInt(query.get('page') || '1');
    const sort = query.get('sort') || 'id';
    const direction = (query.get('direction') as 'asc' | 'desc') || 'desc';
    setSortField(sort);
    setSortDirection(direction);
    setSortOrder(direction);
    loadDxDiagnosisResults(page, sort, direction);
  }, [location.search]);

  const handlePageChange = (page: number) => {
    const query = new URLSearchParams(location.search);
    query.set('page', page.toString());
    navigate({ search: query.toString() });
  };

  const handleSortChange = (field: string) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
    setSortOrder(newDirection);
    const query = new URLSearchParams(location.search);
    query.set('sort', field);
    query.set('direction', newDirection);
    query.set('page', '1'); // ソート変更時はページを1にリセット
    navigate({ search: query.toString() });
  };

  const isLastQuestionInStep = (numberOfAnsweredQuestions: number): boolean => {
    return numberOfAnsweredQuestions % QUESTIONS_PER_STEP === 0;
  };

  const handleResumeAnswer = (
    dxDiagnosisId: number,
    stepId: number,
    numberOfAnsweredQuestions: number,
  ) => {
    if (numberOfAnsweredQuestions < MAX_NUMBER_OF_QUESTIONS) {
      const nextStepId = isLastQuestionInStep(numberOfAnsweredQuestions) ? stepId + 1 : stepId;
      navigate(
        generatePath(COMPANY_URL.DX_DIAGNOSIS_QUESTION, { id: dxDiagnosisId, stepId: nextStepId }),
      );
    } else {
      navigate(generatePath(COMPANY_URL.DX_DIAGNOSIS_ANSWERS, { id: dxDiagnosisId }));
    }
  };

  const handleDeleteClick = (resultId: number) => {
    setDeletingResultId(resultId);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingResultId) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await dxDiagnosisResultApiClient.delete(deletingResultId);
      handleApiResponse(
        response,
        () => {
          setDxDiagnosisResults((prev) => prev.filter((result) => result.id !== deletingResultId));
          toast('削除しました');
          setDeleteModalOpen(false);
          setDeletingResultId(null);
        },
        (error) => {
          logger.error('診断結果の削除に失敗しました:', error);
          toast('削除に失敗しました');
        },
      );
    } catch (error) {
      logger.error('診断結果の削除に失敗しました:', error);
      toast('削除に失敗しました');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setDeletingResultId(null);
  };

  const filterExportOptions = [
    { label: 'すべて', value: 'all' },
    { label: '回答中', value: DxDiagnosisResultStatus.IN_PROGRESS.toString() },
    { label: '回答済み', value: DxDiagnosisResultStatus.COMPLETED.toString() },
  ];

  const handleSelectExportOption = (
    selectedOptions: { value: string; label: React.ReactNode }[],
  ) => {
    const selectedValues = selectedOptions.map((option) => option.value);
    setExportFilters(selectedValues);
  };

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      const statusFilters = exportFilters
        .filter((filter) => filter !== 'all')
        .map((filter) => parseInt(filter));

      const params =
        statusFilters.length === 0 || statusFilters.length === 2 ? {} : { status: statusFilters };

      const response = await dxDiagnosisResultApiClient.exportCsv(params);

      handleApiResponse<Blob, never>(
        response,
        (csvBlob) => {
          const bom = '\uFEFF';
          const blobWithBom = new Blob([bom, csvBlob], { type: 'text/csv;charset=utf-8;' });

          const blobUrl = window.URL.createObjectURL(blobWithBom);
          const anchorElement = document.createElement('a');
          anchorElement.href = blobUrl;
          const fileNamePrefix = 'DX診断結果一覧';
          const currentData = dayjs().format('YYYYMMDD_HHmmss');
          anchorElement.download = `${fileNamePrefix}_${currentData}.csv`;
          document.body.appendChild(anchorElement);
          anchorElement.click();
          document.body.removeChild(anchorElement);
          window.URL.revokeObjectURL(blobUrl);
        },
        (error) => {
          logger.error('CSVのエクスポートに失敗しました。:', error);
          toast('CSVのエクスポートに失敗しました。');
        },
      );
    } catch (error) {
      logger.error('CSVのエクスポートに失敗しました。:', error);
      toast('CSVのエクスポートに失敗しました。');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <main className='px-6 py-12 lg:pt-[105px]'>
      <div>
        <div className='flex flex-col'>
          <Title>DX診断結果一覧</Title>
          <div className='mt-8 flex justify-end'>
            <div className='flex flex-col items-end gap-4 lg:flex-row lg:items-center'>
              <Button
                size='s'
                onClick={() => navigate(COMPANY_URL.DX_DIAGNOSIS_EXPLANATION)}
              >
                DX診断の開始
              </Button>
              {/* <div className='flex gap-3'>
                <DropdownCheckbox
                  className='!w-[150px]'
                  options={filterExportOptions}
                  onSelect={handleSelectExportOption}
                  allSelectedText='すべて'
                  selectedCountText={(count) => `${count}件のステータス`}
                  initialSelectedOptions={filterExportOptions}
                />
                <Button
                  size={isExporting ? 'l' : 's'}
                  className='!px-3 !py-1'
                  onClick={handleExportCsv}
                  loading={isExporting}
                >
                  <SvgIconDownload />
                </Button>
              </div> */}
            </div>
          </div>
        </div>
        <div className='mt-4 overflow-auto'>
          <table className='size-full min-w-[900px] table-fixed text-sm [&_tbody_tr>*]:p-0 [&_thead_th]:p-0'>
            <colgroup>
              <col className='w-[100px]' />
              <col className='w-[120px]' />
              <col className='w-[120px]' />
              <col className='min-w-[125px]' />
              <col className='w-[108px]' />
              <col className='w-[124px]' />
              <col className='w-[180px]' />
            </colgroup>
            <thead className='h-full border border-solid border-gray-200 bg-gray-50'>
              <tr>
                <Th
                  sortable
                  sortOrder={sortOrder && sortField === 'id' ? sortOrder : null}
                  onClick={() => handleSortChange('id')}
                >
                  診断ID
                </Th>
                <Th
                  sortable
                  sortOrder={sortOrder && sortField === 'created_at' ? sortOrder : null}
                  onClick={() => handleSortChange('created_at')}
                >
                  開始日
                </Th>
                <Th
                  sortable
                  sortOrder={sortOrder && sortField === 'diagnosis_at' ? sortOrder : null}
                  onClick={() => handleSortChange('diagnosis_at')}
                >
                  完了日
                </Th>
                <Th
                  sortable
                  sortOrder={
                    sortOrder && sortField === 'companyUser.fullname_kana' ? sortOrder : null
                  }
                  onClick={() => handleSortChange('companyUser.fullname_kana')}
                >
                  回答者
                </Th>
                <Th
                  sortable
                  sortOrder={sortOrder && sortField === 'version' ? sortOrder : null}
                  onClick={() => handleSortChange('version')}
                >
                  診断ver
                </Th>
                <Th
                  sortable
                  sortOrder={sortOrder && sortField === 'status' ? sortOrder : null}
                  onClick={() => handleSortChange('status')}
                >
                  ステータス
                </Th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {dxDiagnosisResults.length > 0 &&
                dxDiagnosisResults.map((result) => (
                  <tr
                    key={result.id}
                    className='[&:nth-child(2n)]:bg-gray-10'
                  >
                    <td>
                      <div className='px-4 py-6'>{result.id}</div>
                    </td>
                    <td>
                      <div className='px-4 py-6'>{result.created_at}</div>
                    </td>
                    <td>
                      <div className='px-4 py-6'>{result.diagnosis_on}</div>
                    </td>
                    <td>
                      <div className='truncate px-4 py-6'>
                        {result.company_user.family_name} {result.company_user.first_name}
                      </div>
                    </td>
                    <td>
                      <div className='px-4 py-6'>{result.version}</div>
                    </td>
                    <td>
                      <div className='px-4 py-6'>{result.status_label}</div>
                    </td>
                    <td>
                      <div className='flex justify-end px-4'>
                        {!result?.is_finished ? (
                          <React.Fragment>
                            {profile?.user?.id === result.company_user.id && (
                              <Button
                                size='s'
                                variant='blue_quaternary'
                                outline
                                onClick={() =>
                                  handleResumeAnswer(
                                    result.id,
                                    result.dx_diagnosis_progresses.last_step_answered_id,
                                    result.dx_diagnosis_progresses.number_of_answered_questions,
                                  )
                                }
                                data-testid='resume-answer-button'
                              >
                                再開する
                              </Button>
                            )}
                          </React.Fragment>
                        ) : (
                          <Button
                            size='s'
                            variant='quaternary'
                            outline
                            onClick={() => navigate(`/dx-diagnosis/${result.id}/results`)}
                            className='whitespace-nowrap'
                          >
                            診断結果を見る
                          </Button>
                        )}
                        {/* <div className='size-6'>
                          <DxDiagnosisMoreVertMenu
                            menuItems={[
                              {
                                label: '削除',
                                onClick: () => handleDeleteClick(result.id),
                                variant: 'warning',
                              },
                            ]}
                          />
                        </div> */}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {pagination && pagination.total !== 0 && (
          <div className='mt-6'>
            <PaginationComponent
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    </main>
  );
};

export default DxDiagnosisListForm;
